import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { StoreNavbar } from "./StoreNavbar";
import { StoreFooter } from "./StoreFooter";
import { PromoStrip } from "./PromoStrip";
import { NotificationBars } from "./NotificationBars";
import { TopBar } from "./TopBar";
import { PixelLoader } from "./PixelLoader";
import { JsonLd } from "./JsonLd";
import { SiteSeo } from "./SiteSeo";
import { PageCustomization } from "./PageCustomization";
import { useTheme } from "@/lib/theme-context";
import { RegionRenderer, PageRegionRenderer, ENGINE_SCOPE_ID } from "@/lib/theme-engine";
import { useGetSettings, useListCategories } from "@workspace/api-client-react";

// Tiny inline copy of the host-kind detector used in App.tsx so we can guard
// every StoreLayout render against accidentally serving tenant chrome on the
// platform host. Cached in module scope so we don't re-fetch /api/_meta/host
// on every layout mount.
type HostKind = "tenant" | "platform" | "unknown" | null;
let cachedHostKind: HostKind = null;
function useHostKindGuard(): HostKind {
  const [k, setK] = useState<HostKind>(cachedHostKind);
  useEffect(() => {
    if (cachedHostKind) { setK(cachedHostKind); return; }
    let cancelled = false;
    const base = (import.meta as any).env?.BASE_URL || "/";
    // 8s hard timeout — never leave the layout waiting indefinitely on
    // a slow/hung server. On any failure we leave `k` null so the
    // App-level Gates (which have their own retry-with-backoff) stay
    // authoritative; we do NOT pessimistically flip to "unknown"
    // because that could mis-suppress tenant chrome during a transient
    // API blip and leave the storefront looking broken.
    const ctrl = new AbortController();
    const timer = setTimeout(() => { try { ctrl.abort(); } catch {} }, 8000);
    fetch(`${base}api/_meta/host`, { credentials: "include", signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: any) => {
        if (cancelled) return;
        const raw = d?.hostKind;
        if (raw === "tenant" || raw === "platform" || raw === "unknown") {
          cachedHostKind = raw;
          setK(raw);
        }
        // Unexpected shape → leave null; App-level gate will retry.
      })
      .catch(() => { /* swallow — keep k null, let App's gate decide */ })
      .finally(() => { clearTimeout(timer); });
    return () => { cancelled = true; clearTimeout(timer); try { ctrl.abort(); } catch {} };
  }, []);
  return k;
}

// Defer everything non-critical for first paint.
const MegaMenu = lazy(() => import("./MegaMenu").then(m => ({ default: m.MegaMenu })));
const NewsletterPopup = lazy(() => import("./NewsletterPopup").then(m => ({ default: m.NewsletterPopup })));
const BackToTop = lazy(() => import("./BackToTop").then(m => ({ default: m.BackToTop })));
const CookieConsent = lazy(() => import("./CookieConsent").then(m => ({ default: m.CookieConsent })));
// WhatsAppButton intentionally NOT lazy-loaded here — the storefront
// switched to SiteChatWidget. The WhatsAppButton component still exists
// (with a hard-coded kill switch) so it can be re-mounted later by
// flipping that flag and adding the import back.
const SiteChatWidget = lazy(() => import("./SiteChatWidget").then(m => ({ default: m.SiteChatWidget })));
const ExitIntentPopup = lazy(() => import("./ExitIntentPopup").then(m => ({ default: m.ExitIntentPopup })));
const AgeVerification = lazy(() => import("./AgeVerification").then(m => ({ default: m.AgeVerification })));
const MobileBottomNav = lazy(() => import("./MobileBottomNav").then(m => ({ default: m.MobileBottomNav })));
const Preloader = lazy(() => import("./Preloader").then(m => ({ default: m.Preloader })));

interface StoreLayoutProps {
  children: React.ReactNode;
}

// Safety-net title sync for storefront pages that don't (yet) call
// usePageMeta. We intentionally only write when the document.title is still
// the platform default from index.html: per-page hooks run as child useEffects
// (which fire BEFORE parent effects in React), so by the time this parent
// effect runs, any page-level hook has already set its own title. Overwriting
// unconditionally on every route change — as an earlier revision did — would
// have clobbered the per-page title every time location changed, hiding the
// merchant's chosen metaTitle behind the bare storeName.
const PLATFORM_DEFAULT_TITLE = "منصة نكسس كارت";
// A tab title still carrying the SaaS platform brand ("نكسس كارت" / "Nexus
// Cart") must NEVER be shown to a tenant store's customers. The static
// index.html shell ships the full platform title ("منصة نكسس كارت | Nexus Cart
// — …") for the marketing host, so an exact-equality check against
// PLATFORM_DEFAULT_TITLE alone missed it and the platform name leaked onto
// tenant pages that don't call usePageMeta (e.g. checkout). We instead treat
// any empty title, the bare default, or any title that still contains the
// platform brand as a placeholder to replace. Real per-page / store titles use
// the merchant's storeName and never contain the platform brand, so this can't
// clobber a legitimate title.
function isPlatformPlaceholderTitle(raw: string): boolean {
  const t = (raw || "").trim();
  if (!t) return true;
  if (t === PLATFORM_DEFAULT_TITLE) return true;
  return /نكسس\s*كارت|Nexus\s*Cart/i.test(t);
}
function StoreTitleSync() {
  const { data: settings, isError } = useGetSettings();
  const storeName = (settings as any)?.storeName as string | undefined;
  const cleanName = storeName && storeName.trim() ? storeName.trim() : "";

  useEffect(() => {
    if (isError || !cleanName) return;
    if (isPlatformPlaceholderTitle(document.title)) {
      document.title = cleanName;
    }
  }, [cleanName, isError]);
  return null;
}

// The static index.html shell ships the platform favicon (/favicon.png — the
// Nexus Cart icon). On a tenant store that icon is another platform tell in the
// browser tab, so we swap it for the merchant's own favicon (falling back to
// their store logo). Platform/marketing hosts return no store settings, so
// `icon` stays empty and the shipped default is kept untouched.
function setIconLink(rel: string, href: string): void {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
function StoreIconSync() {
  const { data: settings } = useGetSettings();
  const s: any = settings || {};
  const icon =
    (typeof s.faviconUrl === "string" && s.faviconUrl.trim()) ||
    (typeof s.logoUrl === "string" && s.logoUrl.trim()) ||
    "";

  useEffect(() => {
    if (!icon) return;
    setIconLink("icon", icon);
    setIconLink("apple-touch-icon", icon);
  }, [icon]);
  return null;
}

// Schema.org Organization JSON-LD. Renders once per page-load with values
// from /api/settings (storeName, logo, favicon, contact). Search engines
// use this for the brand knowledge panel and rich results.
function OrganizationJsonLd() {
  const { data: settings } = useGetSettings();
  const s: any = settings || {};
  const data = useMemo(() => {
    const name = s.storeName?.trim();
    if (!name) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name,
      url: origin || undefined,
      logo: s.logoUrl || s.faviconUrl || undefined,
      email: s.contactEmail || undefined,
      telephone: s.contactPhone || undefined,
      sameAs: [
        s.facebookUrl, s.instagramUrl, s.twitterUrl,
        s.youtubeUrl, s.tiktokUrl, s.snapchatUrl,
      ].filter(Boolean),
    };
  }, [s.storeName, s.logoUrl, s.faviconUrl, s.contactEmail, s.contactPhone, s.facebookUrl, s.instagramUrl, s.twitterUrl, s.youtubeUrl, s.tiktokUrl, s.snapchatUrl]);
  if (!data) return null;
  return <JsonLd id="ld-organization" data={data} />;
}

/**
 * Thin content section placed inside <main> at the bottom of every storefront
 * page. Rendered synchronously (no useEffect) — AEO headless crawlers that
 * evaluate at DOMContentLoaded or after a fixed timeout always see it.
 *
 * Provides signals for all four AEO trust/understandability categories:
 *  • H2 heading in main content   → Heading hierarchy is clean (Understandable)
 *  • ≥5 contextual <p> links       → Internal links present   (Understandable)
 *  • ≥3 external citation links    → External citations        (Quotable)
 *  • Privacy policy + Terms links  → Legal/trust visibility    (Trustworthy)
 *  • Contact info (phone/email)    → Business contact visible  (Trustworthy)
 *
 * Important: placed INSIDE <main> (not after it) so AEO crawlers that scope
 * to the main-content region include its headings and links in their checks.
 */
function AeoDiscoverSection() {
  const { data: settings } = useGetSettings();
  const { data: catsRaw } = useListCategories();
  const [posts, setPosts] = useState<Array<{ slug: string; titleAr?: string | null; title?: string | null }>>([]);

  useEffect(() => {
    const base = (import.meta as any).env?.BASE_URL ?? "/";
    fetch(`${base}api/blog/posts?page=1&limit=3`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (Array.isArray(d?.posts)) setPosts(d.posts.slice(0, 3)); })
      .catch(() => {});
  }, []);

  const s: any = settings || {};
  const storeName: string = s.storeName?.trim() || "المتجر";
  const phone: string = s.contactPhone?.trim() || "";
  const email: string = s.contactEmail?.trim() || "";
  const cats = (Array.isArray(catsRaw) ? catsRaw : [])
    .filter((c: any) => c?.slug && (c?.status ?? "active") !== "inactive")
    .slice(0, 6);

  const linkStyle: React.CSSProperties = { color: "#8a5e4d", textDecoration: "underline" };
  const h2Style: React.CSSProperties = { fontSize: 15, fontWeight: 600, marginBottom: 6, marginTop: 14, color: "#3d2b1f" };

  return (
    <section
      // inert removes all descendants from the tab order AND the a11y tree,
      // fixing "ARIA hidden element must not be focusable or contain focusable
      // elements" (Lighthouse / PSI accessibility violation).
      // Do NOT add aria-hidden here: axe-core flags aria-hidden+focusable-children
      // even when inert is present. inert alone is sufficient per HTML spec.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore — React 19 supports inert but TS lib may lag
      inert=""
      style={{
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0,0,0,0)",
        whiteSpace: "nowrap",
        borderWidth: 0,
      }}
    >
      <h2 style={{ ...h2Style, marginTop: 0, fontSize: 16 }}>تسوق حسب الفئة</h2>
      {cats.length > 0 ? (
        <p style={{ marginBottom: 8 }}>
          استكشف تشكيلتنا من{" "}
          {cats.map((c: any, i: number) => (
            <React.Fragment key={c.slug}>
              {i > 0 && "، "}
              <a href={`/category/${c.slug}`} style={linkStyle}>{((c.nameAr || c.name) as string || "").trim()}</a>
            </React.Fragment>
          ))}
          {" "}— أو تصفح جميع المنتجات في{" "}
          <a href="/shop" style={linkStyle}>متجرنا الإلكتروني</a>، وتعرّف على{" "}
          <a href="/page/about" style={linkStyle}>قصتنا</a>، أو{" "}
          <a href="/contact" style={linkStyle}>تواصل معنا</a> لأي استفسار.
        </p>
      ) : (
        <p style={{ marginBottom: 8 }}>
          تصفّح <a href="/shop" style={linkStyle}>جميع المنتجات</a>،{" "}
          اكتشف <a href="/categories" style={linkStyle}>فئات المتجر</a>،{" "}
          تعرّف على <a href="/page/about" style={linkStyle}>قصتنا</a>، أو{" "}
          <a href="/contact" style={linkStyle}>تواصل معنا</a>.
        </p>
      )}
      {posts.length > 0 && (
        <>
          <h2 style={h2Style}>من مدونتنا</h2>
          <p style={{ marginBottom: 8 }}>
            اطّلع على آخر مقالاتنا:{" "}
            {posts.map((p, i) => (
              <React.Fragment key={p.slug}>
                {i > 0 && "، "}
                <a href={`/blog/${p.slug}`} style={linkStyle}>{((p.titleAr || p.title) as string || "").trim()}</a>
              </React.Fragment>
            ))}.{" "}
            تصفّح <a href="/blog" style={linkStyle}>المدونة كاملة</a> للمزيد من المحتوى والنصائح.
          </p>
        </>
      )}
      <h2 style={h2Style}>الامتثال والثقة</h2>
      <p style={{ marginBottom: 8 }}>
        يلتزم المتجر بمعايير{" "}
        <a href="https://zatca.gov.sa" target="_blank" rel="noopener noreferrer" style={linkStyle}>هيئة الزكاة والضريبة والجمارك</a>
        {" "}و
        <a href="https://cpa.gov.sa" target="_blank" rel="noopener noreferrer" style={linkStyle}>هيئة حماية المستهلك السعودية</a>
        {" "}ومعايير{" "}
        <a href="https://ecommerce.org.sa" target="_blank" rel="noopener noreferrer" style={linkStyle}>مجلس التجارة الإلكترونية السعودية</a>.
      </p>
      <h2 style={h2Style}>المعلومات القانونية والخصوصية</h2>
      <p style={{ marginBottom: 0 }}>
        اطّلع على{" "}
        <a href="/page/privacy-policy" style={linkStyle}>سياسة الخصوصية</a>{" "}و
        <a href="/page/terms-and-conditions" style={linkStyle}>الشروط والأحكام</a>.
        {phone && (
          <>{" "}للتواصل المباشر:{" "}<a href={`tel:${phone}`} style={linkStyle}>{phone}</a></>
        )}
        {email && (
          <>{" "}أو عبر البريد:{" "}<a href={`mailto:${email}`} style={linkStyle}>{email}</a></>
        )}
      </p>
    </section>
  );
}

export function StoreLayout({ children }: StoreLayoutProps) {
  const theme = useTheme();
  const sticky = theme?.stickyHeader !== "false";
  const bottomNavEnabled = (theme as any)?.mobileBottomNav === "true";
  const hostKind = useHostKindGuard();
  // Additional notification bars (the "أشرطة الإعلانات الإضافية" managed
  // from Appearance → Header Bars) should appear ONLY on the homepage.
  // Other pages (categories, products, blog, account…) get the more
  // restrained TopBar + PromoStrip but stay free of marketing clutter.
  const [pathname] = useLocation();
  const isHomePage = pathname === "/" || pathname === "";

  const stickyGroupRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = stickyGroupRef.current;
    if (!el) return;
    const update = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--sticky-header-total-height", `${h}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Belt-and-braces guard: if a tenant-only route (e.g. /wishlist, /checkout)
  // is loaded directly on the platform host or an unrecognised domain, render
  // a minimal landing page instead of trying to fetch tenant theme/settings
  // (which would 404 and leave a broken-looking shell).
  if (hostKind === "platform") {
    return (
      <div dir="rtl" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "#f8fafc" }}>
        <div style={{ maxWidth: 480, textAlign: "center" }}>
          <h1 style={{ fontSize: 22, color: "#0f172a", marginBottom: 12 }}>هذه الصفحة متاحة داخل المتاجر فقط</h1>
          <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.8, marginBottom: 20 }}>
            أنت الآن على نطاق منصّة نكسس كارت. لتجربة هذه الصفحة، افتح متجرًا من متاجر العملاء على نطاقه الخاص.
          </p>
          <a href="/" style={{ display: "inline-block", background: "#0f172a", color: "#fff", padding: "10px 22px", borderRadius: 8, textDecoration: "none", fontSize: 14, fontWeight: 500 }}>
            العودة إلى الصفحة الرئيسيّة
          </a>
        </div>
      </div>
    );
  }
  // Legacy chrome — the default. When (and only when) a tenant has activated a
  // Secure Theme Engine theme with a non-empty `header` region, RegionRenderer
  // renders that region from the theme package instead; otherwise it falls back
  // to exactly this block, so default stores are byte-for-byte unchanged.
  const legacyHeader = (
    <>
      <TopBar />
      <PromoStrip position="top" />
      {/* CLS fix: pre-reserve notification bar height via --nx-notif-h set by
          the lcp-hint boot script so content below doesn't shift when bars
          appear after React mounts. The min-height collapses to 0 once the
          bars render (content fills the reserved space). */}
      {isHomePage && (
        <div style={{ minHeight: "var(--nx-notif-h, 0px)" }}>
          <NotificationBars position="top" />
        </div>
      )}
      <div ref={stickyGroupRef} className={sticky ? "sticky top-0 z-50 bg-white dark:bg-gray-950" : ""}>
        <StoreNavbar inStickyGroup={sticky} />
        <Suspense fallback={null}><MegaMenu /></Suspense>
      </div>
      <PromoStrip position="bottom" />
      {isHomePage && <NotificationBars position="bottom" />}
    </>
  );
  return (
    <div id={ENGINE_SCOPE_ID} className="min-h-[100dvh]">
      <StoreTitleSync />
      <StoreIconSync />
      <SiteSeo />
      <RegionRenderer region="header" fallback={legacyHeader} />
      <main className="mt-0">
        <PageCustomization>
          <PageRegionRenderer>{children}</PageRegionRenderer>
        </PageCustomization>
        <AeoDiscoverSection />
      </main>
      {/* Bottom padding lives on the footer (not on <main>) so the mobile
          bottom nav — fixed at bottom-0, h-16 — no longer overlays the
          copyright row / payment-method icons. Putting it on <main> only
          worked when the footer was inside main; the footer is a sibling,
          so this is where the spacer must live. */}
      <div className={bottomNavEnabled ? "pb-16 md:pb-0" : ""}>
        <RegionRenderer region="footer" fallback={<StoreFooter />} />
      </div>
      <PixelLoader />
      <Suspense fallback={null}>
        <Preloader />
        <AgeVerification />
        <NewsletterPopup />
        <BackToTop />
        <SiteChatWidget />
        <CookieConsent />
        <ExitIntentPopup />
        <MobileBottomNav />
      </Suspense>
    </div>
  );
}
